import React, { useEffect, useState } from 'react';
import {
  BriefcaseBusiness,
  CalendarCheck,
  MessageCircle,
  Star,
  UtensilsCrossed,
} from 'lucide-react';
import { FOODSTATION_PRODUCTS, DEFAULT_THEME, directionsUrl } from '../constants';
import { resolveTheme } from '../theme';
import { buildButtonOrderItems, orderButtonItems } from '../utils/buttonOrder';

/**
 * Renderização pura da página pública de links (a "bio" do restaurante).
 * Reutilizada pela página pública em tela cheia e pela prévia no celular.
 * Toda a aparência vem de variáveis CSS resolvidas de `theme` (resolveTheme).
 *
 * Props:
 *  - restaurant: { name, tagline, monogram, address, hours, verified,
 *                  coverImage, profileImage }
 *  - products:   [{ id, enabled, label }]
 *  - externalLinks: [{ id, label, url, image }]
 *  - buttonOrder: ["product:reservo", "external:abc", "directions"]
 *  - theme: { presetId, accent, buttonStyle, customColors }
 *  - compact: reduz espaçamentos (prévia)
 *  - interactive: se true, links externos navegam
 */
const productMeta = (id) => FOODSTATION_PRODUCTS.find((product) =>
  product.id === (String(id).startsWith('onemenu_') ? 'onemenu' : id)
);

const PRODUCT_ICONS = {
  reservo: CalendarCheck,
  reservo_waitlist: CalendarCheck,
  onemenu: UtensilsCrossed,
  onemenu_delivery: UtensilsCrossed,
  onemenu_in_person: UtensilsCrossed,
  feedbackhero: Star,
  curriculocerto: BriefcaseBusiness,
  atendabot: MessageCircle,
};

const ProductIcon = ({ id }) => {
  const Icon = PRODUCT_ICONS[id] || Star;
  return <Icon size={21} strokeWidth={2.35} />;
};

const Arrow = () => (
  <span className="fsl-link-arrow" aria-hidden>
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
      <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  </span>
);

const ProductButton = ({ meta, label, interactive }) => {
  const href = interactive ? meta.url : null;
  const isDisabled = interactive && !href;
  const isExternal = href && /^https?:\/\//i.test(href);

  return (
    <a
      className={`fsl-link-btn ${isDisabled ? 'is-disabled' : ''}`}
      href={href || '#!'}
      target={isExternal ? '_blank' : undefined}
      rel={isExternal ? 'noopener noreferrer' : undefined}
      aria-disabled={isDisabled ? 'true' : undefined}
      onClick={href ? undefined : (e) => e.preventDefault()}
    >
      <span className="fsl-link-icon fsl-link-icon--product" aria-hidden>
        <ProductIcon id={meta.id} />
      </span>
      <span className="fsl-link-text">
        <span className="fsl-link-label">{label}</span>
      </span>
      <Arrow />
    </a>
  );
};

const ExternalIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
    <path d="M10 13a5 5 0 007.07 0l1.42-1.42a5 5 0 00-7.07-7.07L10.6 5.76" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 11a5 5 0 00-7.07 0L5.5 12.42a5 5 0 007.07 7.07L13.4 18.24" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const ExternalButton = ({ item, interactive }) => {
  const [showHint, setShowHint] = useState(false);
  const isExternal = /^https?:\/\//i.test(item.url || '');

  useEffect(() => {
    if (!interactive || !item.previewHint) return undefined;
    const timeoutId = setTimeout(() => setShowHint(true), 3000);
    return () => clearTimeout(timeoutId);
  }, [interactive, item.previewHint]);

  return (
    <div className="fsl-link-entry">
      {showHint ? (
        <div className="fsl-link-hint" role="status">
          <span>{item.previewHint}</span>
          <button
            type="button"
            className="fsl-link-hint-close"
            aria-label="Fechar aviso do atendimento automatizado"
            onClick={() => setShowHint(false)}
          >
            ×
          </button>
        </div>
      ) : null}
      <a
        className="fsl-link-btn fsl-link-btn--external"
        href={interactive ? item.url : undefined}
        target={interactive && isExternal ? '_blank' : undefined}
        rel={interactive && isExternal ? 'noopener noreferrer' : undefined}
        onClick={interactive ? undefined : (e) => e.preventDefault()}
      >
        <span
          className={`fsl-link-icon ${item.image ? 'fsl-link-icon--img' : 'fsl-link-icon--external'}`}
          aria-hidden
        >
          {item.image ? <img src={item.image} alt="" /> : <ExternalIcon />}
        </span>
        <span className="fsl-link-text">
          <span className="fsl-link-label">{item.label || 'Link externo'}</span>
        </span>
        <Arrow />
      </a>
    </div>
  );
};

const DirectionsButton = ({ address, interactive }) => (
  <a
    className="fsl-link-btn fsl-link-btn--external"
    href={interactive ? directionsUrl(address) : undefined}
    target={interactive ? '_blank' : undefined}
    rel={interactive ? 'noopener noreferrer' : undefined}
    onClick={interactive ? undefined : (e) => e.preventDefault()}
  >
    <span className="fsl-link-icon fsl-link-icon--external" aria-hidden>
      <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
        <path d="M3 11l18-8-8 18-2-8-8-2z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    </span>
    <span className="fsl-link-text">
      <span className="fsl-link-label">Como chegar</span>
    </span>
    <Arrow />
  </a>
);

const PinIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M12 21s7-6.1 7-11a7 7 0 10-14 0c0 4.9 7 11 7 11z" stroke="currentColor" strokeWidth="2" strokeLinejoin="round" />
    <circle cx="12" cy="10" r="2.4" stroke="currentColor" strokeWidth="2" />
  </svg>
);
const ClockIcon = () => (
  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" aria-hidden>
    <circle cx="12" cy="12" r="8.5" stroke="currentColor" strokeWidth="2" />
    <path d="M12 7.5V12l3 1.8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

const LinksPageView = ({
  restaurant,
  products = [],
  externalLinks = [],
  buttonOrder = [],
  theme = DEFAULT_THEME,
  compact = false,
  interactive = false,
}) => {
  const promotion = restaurant?.promotion || {};
  const [showPromotion, setShowPromotion] = useState(Boolean(promotion.enabled && promotion.image));
  const showDirections = restaurant.showDirections !== false && !!restaurant.address;
  const buttonItems = buildButtonOrderItems({ products, externalLinks, restaurant });
  const orderedButtonItems = orderButtonItems(buttonItems, buttonOrder);
  const hasButtons = orderedButtonItems.length > 0;
  const resolved = resolveTheme(theme);
  const styleObj = { ...resolved.vars, background: resolved.preset.cardBg };
  const hideIcons = theme.showIcons === false;

  useEffect(() => {
    setShowPromotion(Boolean(promotion.enabled && promotion.image));
  }, [promotion.enabled, promotion.image]);

  const coverStyle = restaurant.coverImage
    ? { backgroundImage: `url(${restaurant.coverImage})`, backgroundSize: 'cover', backgroundPosition: 'center' }
    : { background: resolved.cover };

  return (
    <div
      className={`fsl-view fsl-view--${resolved.mode} fsl-btn-${theme.buttonStyle || resolved.preset.defaultButtonStyle} ${hideIcons ? 'fsl-noicons' : ''} ${compact ? 'fsl-view--compact' : ''}`}
      style={styleObj}
    >
      <div
        className={`fsl-cover ${theme.cleanCover ? 'fsl-cover--clean' : ''}`}
        style={coverStyle}
      >
        <div className="fsl-cover-shade" aria-hidden />
      </div>

      <div className="fsl-head">
        <div className="fsl-logo">
          {restaurant.profileImage ? (
            <img src={restaurant.profileImage} alt={restaurant.name} />
          ) : (
            <span>{restaurant.monogram}</span>
          )}
        </div>
        <div className="fsl-namewrap">
          <h1 className="fsl-name">{restaurant.name}</h1>
          {restaurant.verified && (
            <span className="fsl-verified" title="Perfil verificado" aria-label="Perfil verificado">
              <svg width="13" height="13" viewBox="0 0 24 24" fill="none">
                <path d="M5 12.5l4.2 4.2L19 7" stroke="currentColor" strokeWidth="2.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </span>
          )}
        </div>
        {restaurant.tagline && <p className="fsl-tagline">{restaurant.tagline}</p>}
        {((restaurant.showAddress && restaurant.address) ||
          (restaurant.showHours && restaurant.hours) || restaurant.openStatus) && (
          <div className="fsl-info">
            {restaurant.openStatus && (
              <span
                className={`fsl-info-item fsl-open-status ${restaurant.openStatus.isOpen ? 'is-open' : 'is-closed'}`}
                role="status"
              >
                <span className="fsl-open-status-dot" aria-hidden />
                <span>
                  <strong>{restaurant.openStatus.label}</strong>
                  {restaurant.openStatus.detail ? ` · ${restaurant.openStatus.detail}` : ''}
                </span>
              </span>
            )}
            {restaurant.showAddress && restaurant.address && (
              <span className="fsl-info-item">
                <PinIcon /> {restaurant.address}
              </span>
            )}
            {restaurant.showHours && restaurant.hours && (
              <span className="fsl-info-item">
                <ClockIcon /> {restaurant.hours}
              </span>
            )}
          </div>
        )}
      </div>

      <div className="fsl-links">
        {orderedButtonItems.map((item) => {
          if (item.type === 'product') {
            const meta = { ...(productMeta(item.product.id) || {}), ...item.product };
            if (!meta.id) return null;
            return (
              <ProductButton
                key={item.key}
                meta={meta}
                label={item.product.label || meta.defaultLabel}
                interactive={interactive}
              />
            );
          }

          if (item.type === 'external') {
            return (
              <ExternalButton
                key={item.key}
                item={item.link}
                interactive={interactive}
              />
            );
          }

          if (item.type === 'directions' && showDirections) {
            return (
              <DirectionsButton
                key={item.key}
                address={restaurant.address}
                interactive={interactive}
              />
            );
          }

          return null;
        })}

        {!hasButtons && (
          <div className="fsl-empty">
            <p>Nenhum botão ativo ainda.</p>
            <span>Ative um produto ou adicione um link externo.</span>
          </div>
        )}
      </div>

      <div className="fsl-foot">
        <span className="fsl-foot-brand">
          <span className="fsl-foot-lock" aria-hidden>
            <svg width="11" height="11" viewBox="0 0 24 24" fill="none">
              <rect x="5" y="11" width="14" height="9" rx="2" stroke="currentColor" strokeWidth="2" />
              <path d="M8 11V8a4 4 0 018 0v3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" />
            </svg>
          </span>
          Powered by <strong>FoodStation</strong>
        </span>
      </div>

      {showPromotion && promotion.image && (
        <div className="fsl-promotion-backdrop" role="presentation">
          <div
            className="fsl-promotion-modal"
            role="dialog"
            aria-modal="true"
            aria-label="Promoção do restaurante"
          >
            <button
              type="button"
              className="fsl-promotion-close"
              aria-label="Fechar promoção"
              onClick={() => setShowPromotion(false)}
            >
              ×
            </button>
            <img
              src={promotion.image}
              alt={`Promoção de ${restaurant.name || 'restaurante'}`}
            />
          </div>
        </div>
      )}
    </div>
  );
};

export default LinksPageView;
